import React, { useState, useCallback, useMemo } from 'react';
import { useAsyncList } from '@react-stately/data';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useInfiniteFiles } from '@/hooks/queries/useFiles';
import { useDebounce } from '@/hooks/useDebounce';
import { FileMetadata } from '@/services/apis/files';
import MediaLibrary from '@/components/media-library/MediaLibrary';
import { AcceptedFileType, FILE_TYPE_CATEGORIES, isFileUploadType } from '@/components/base/file-upload/utils';

export interface MediaLibraryContainerProps {
  onFileSelect?: (file: FileMetadata) => void;
  fileType?: string; // MIME type filter (e.g., 'image/*', 'video/*', 'application/pdf'). Default: all types
  acceptedTypes?: AcceptedFileType[]; // Filter files by accepted types (client-side filtering for multiple types)
  selectedFileId?: string; // ID of the currently selected file
}

const MediaLibraryContainer: React.FC<MediaLibraryContainerProps> = ({
  onFileSelect,
  fileType,
  acceptedTypes,
  selectedFileId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Filter out YouTube from acceptedTypes for MediaLibrary (it only shows S3 files)
  const fileUploadTypes = useMemo(() => {
    return acceptedTypes?.filter((type) => isFileUploadType(type)) || [];
  }, [acceptedTypes]);

  // Use fileType if provided, otherwise try to derive from acceptedTypes
  const effectiveFileType = useMemo(() => {
    if (fileType) return fileType;
    if (fileUploadTypes.length === 1) {
      const type = fileUploadTypes[0];
      if (type === AcceptedFileType.IMAGE) return 'image/*';
      if (type === AcceptedFileType.VIDEO) return 'video/*';
      if (type === AcceptedFileType.PDF) return 'application/pdf';
    }
    return undefined;
  }, [fileType, fileUploadTypes]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError, error } = useInfiniteFiles({
    search: debouncedSearch,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    provider: 's3',
    fileType: effectiveFileType,
    isMediaLibrary: true,
  });

  // Filter files client-side if multiple acceptedTypes are specified
  const files = useMemo(() => {
    const allFiles = data?.pages.flatMap((page) => page.data) || [];

    // If we have multiple acceptedTypes, filter client-side
    if (fileUploadTypes.length > 1) {
      return allFiles.filter((file) => {
        return fileUploadTypes.some((type) => {
          const category = FILE_TYPE_CATEGORIES[type];
          // Check if file.type matches any of the category's MIME types
          // Since category.mimeTypes contains specific types like 'image/jpeg', 'image/png', etc.
          // we check if file.type starts with the base type (e.g., 'image/' for images)
          const baseType = category.mimeTypes[0]?.split('/')[0];
          if (baseType) {
            return file.type.startsWith(baseType + '/');
          }
          // Fallback: check exact match
          return (category.mimeTypes as readonly string[]).includes(file.type);
        });
      });
    }

    return allFiles;
  }, [data, fileUploadTypes]);

  const list = useAsyncList<FileMetadata>({
    async load({ cursor }: { cursor?: string }) {
      if (cursor) {
        await fetchNextPage();
      }
      return {
        items: files,
        cursor: hasNextPage ? 'next' : undefined,
      };
    },
  });

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  const handleFileSelect = useCallback(
    (file: FileMetadata) => {
      onFileSelect?.(file);
    },
    [onFileSelect],
  );

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">
          {error instanceof Error ? error.message : 'Failed to load files'}
        </p>
      </div>
    );
  }

  return (
    <>
      <MediaLibrary
        files={files}
        isLoading={isLoading}
        isLoadingMore={isFetchingNextPage}
        hasNextPage={hasNextPage || false}
        onFileSelect={handleFileSelect}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        loaderRef={loaderRef}
        selectedFileId={selectedFileId}
      />
    </>
  );
};

export default MediaLibraryContainer;
